// RecentBroadcast.jsx
import React, { useState, useEffect, useRef } from 'react';
import { apiCall } from '../../utils/api';
import toast from 'react-hot-toast';
import PageHeader from '../../components/ui/page-header';
import { Card } from '../../components/ui/card';
import { Table, THead, TBody, Th, Tr } from '../../components/ui/table';
import Pagination from '../../components/ui/pagination';
import { Badge } from '../../components/ui/status-badge';

function RecentBroadcast() {
    const [broadcasts, setBroadcasts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10,
        totalRecords: 0,
        totalPages: 1
    });

    // ─── Modal state ───
    const [modalOpen, setModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [modalClosing, setModalClosing] = useState(false);
    const scrollYRef = useRef(0);

    // ─── Fetch ───
    const fetchBroadcasts = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: pagination.perPage
            };
            const result = await apiCall('recent-broadcast', params, true);

            if (result.status === true) {
                setBroadcasts(result.data || []);
                if (result.pagination) {
                    setPagination(result.pagination);
                }
            } else {
                toast.error(result.message || 'Failed to fetch broadcasts');
                setBroadcasts([]);
            }
        } catch (error) {
            toast.error('Network error. Please try again.');
            setBroadcasts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBroadcasts(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchBroadcasts(newPage);
        }
    };

    // ─── Modal open / close with fade + scroll lock ───
    const openModal = (row) => {
        // Save scroll position and lock body
        scrollYRef.current = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollYRef.current}px`;
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';

        setModalClosing(false);
        setModalData(row);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalClosing(true);
        // Wait for fade-out animation (~200ms) then unmount & unlock body
        setTimeout(() => {
            setModalOpen(false);
            setModalClosing(false);
            setModalData(null);

            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            window.scrollTo(0, scrollYRef.current);
        }, 200);
    };

    // ─── Helpers ───
    const getChannelInfo = (channel) => {
        const map = {
            '1': { label: 'Push Notification', tone: 'brand' },
            '2': { label: 'Email', tone: 'emerald' },
            '3': { label: 'SMS', tone: 'amber' },
            '4': { label: 'WhatsApp', tone: 'violet' }
        };
        return map[channel] || { label: '—', tone: 'slate' };
    };

    const getStatusInfo = (status) => {
        const map = {
            '0': { label: 'Pending', tone: 'amber' },
            '3': { label: 'Ongoing', tone: 'green' },
            '1': { label: 'Completed', tone: 'emerald' },
            '2': { label: 'Cancelled', tone: 'rose' }
        };
        return map[status] || { label: '—', tone: 'slate' };
    };

    const prettyJson = (value) => {
        if (value === null || value === undefined) return '—';
        try {
            const obj = typeof value === 'string' ? JSON.parse(value) : value;
            return JSON.stringify(obj, null, 2);
        } catch {
            return String(value);
        }
    };

    return (
        <>
            <PageHeader
                title="Recent Broadcasts"
                description="View previously sent broadcasts and their delivery details"
            />

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                    Showing{' '}
                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                        {broadcasts.length}
                    </span>{' '}
                    broadcasts
                    {pagination.totalRecords > 0 && ` of ${pagination.totalRecords}`}
                </p>
                {loading && (
                    <div className="flex items-center text-sm text-slate-500 dark:text-slate-400">
                        <svg className="animate-spin h-4 w-4 mr-2 text-brand-600 dark:text-brand-400" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Loading...
                    </div>
                )}
            </div>

            <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                    <Table>
                        <THead>
                            <tr>
                                <Th>ID</Th>
                                <Th>Channel</Th>
                                <Th>Status</Th>
                                <Th>Total</Th>
                                <Th>Success</Th>
                                <Th>Failed</Th>
                                <Th>Audience</Th>
                                <Th>Message</Th>
                            </tr>
                        </THead>
                        <TBody>
                            {broadcasts.length > 0 ? (
                                broadcasts.map((b) => {
                                    const ch = getChannelInfo(b.channel);
                                    const st = getStatusInfo(b.status);
                                    return (
                                        <Tr key={b.broadcast_id}>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-100">
                                                #{b.broadcast_id}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <Badge tone={ch.tone}>{ch.label}</Badge>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <Badge tone={st.tone}>{st.label}</Badge>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700 dark:text-slate-200">
                                                {b.total}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                                                {b.success}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-rose-600 dark:text-rose-400 font-medium">
                                                {b.failed}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <button
                                                    onClick={() => openModal(b)}
                                                    className="text-sm text-brand-600 dark:text-brand-400 underline cursor-pointer hover:text-brand-700 dark:hover:text-brand-300"
                                                >
                                                    View JSON
                                                </button>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <button
                                                    onClick={() => openModal(b)}
                                                    className="text-sm text-brand-600 dark:text-brand-400 underline cursor-pointer hover:text-brand-700 dark:hover:text-brand-300"
                                                >
                                                    View JSON
                                                </button>
                                            </td>
                                        </Tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="8" className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                                        <div className="flex flex-col items-center">
                                            <svg className="w-12 h-12 text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                            </svg>
                                            <p className="text-sm">No broadcasts found</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </TBody>
                    </Table>
                </div>
            </Card>

            <Pagination
                page={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
            />

            {/* ═══════════════ FADE MODAL ═══════════════ */}
            {modalOpen && modalData && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop with fade */}
                    <div
                        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ${modalClosing ? 'opacity-0' : 'opacity-100'
                            }`}
                        onClick={closeModal}
                    />

                    {/* Modal panel with fade + slight scale */}
                    <div
                        className={`relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col transition-all duration-200 ${modalClosing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
                            }`}
                    >
                        {/* Header (sticky) */}
                        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
                            <div>
                                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                    Broadcast #{modalData.broadcast_id}
                                </h3>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                    <Badge tone={getChannelInfo(modalData.channel).tone}>
                                        {getChannelInfo(modalData.channel).label}
                                    </Badge>
                                    <Badge tone={getStatusInfo(modalData.status).tone}>
                                        {getStatusInfo(modalData.status).label}
                                    </Badge>
                                    <span className="text-xs text-slate-500 dark:text-slate-400">
                                        Total: {modalData.total} · Success: {modalData.success} · Failed: {modalData.failed}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={closeModal}
                                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                                aria-label="Close"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Scrollable body */}
                        <div className="overflow-y-auto px-6 py-4 space-y-4">
                            {/* Audience JSON */}
                            <div>
                                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                                    Audience
                                </h4>
                                <pre className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-lg p-3 text-xs text-slate-800 dark:text-slate-200 whitespace-pre-wrap break-all overflow-x-auto">
                                    {prettyJson(modalData.audience)}
                                </pre>
                            </div>

                            {/* Message JSON */}
                            <div>
                                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                                    Message
                                </h4>
                                <pre className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-lg p-3 text-xs text-slate-800 dark:text-slate-200 whitespace-pre-wrap break-all overflow-x-auto">
                                    {prettyJson(modalData.message)}
                                </pre>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end px-6 py-4 border-t border-slate-200 dark:border-slate-700 shrink-0">
                            <button
                                onClick={closeModal}
                                className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-100 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default RecentBroadcast;